import { useState, useEffect } from 'react';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');
const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

const PaymentPage = ({ user, onBack, updateUser, buildUrl }) => {
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  // Check URL for Stripe redirect result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('stripe');
    if (status === 'success' || status === 'canceled') {
      setStripeStatus(status);
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
  }, []);

  const refreshUser = async () => {
    if (!user?.email || !buildUrl || !updateUser) return;
    try {
      const res = await fetch(buildUrl('/me'), {
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user.email,
        },
      });
      if (res.ok) {
        const data = await res.json();
        updateUser({
          ...(data.name ? { name: data.name } : {}),
          requestLimit: data.requestLimit,
          isPro: data.isPro,
          tier: data.tier,
          stripeSubscriptionStatus: data.stripeSubscriptionStatus,
          subscriptionCurrentPeriodEnd: data.subscriptionCurrentPeriodEnd,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd,
          hasStripeCustomer: data.hasStripeCustomer,
        });
      }
    } catch {}
  };

  const handleUpgrade = async () => {
    if (!user?.email) return;
    setError(null);
    setUpgradeLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user.email,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to start checkout');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setUpgradeLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user?.email) return;
    setCancelLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user.email,
        },
      });
      if (res.ok) {
        setCancelSuccess(true);
        setShowCancelConfirm(false);
        await refreshUser();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || 'Failed to cancel subscription');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setCancelLoading(false);
  };

  const isPro = user?.isPro || false;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <button onClick={onBack} style={styles.backBtn}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="#94a3b8">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          Back
        </button>

        <h2 style={styles.heading}>Titler Pro</h2>

        {/* Success banner */}
        {stripeStatus === 'success' && (
          <div style={{
            padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
            background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.2)',
            textAlign: 'center',
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#22c55e' }}>
              Payment successful! Welcome to Pro.
            </span>
          </div>
        )}

        {/* Cancel success banner */}
        {cancelSuccess && (
          <div style={{
            padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
            background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.2)',
            textAlign: 'center',
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#fbbf24' }}>
              Subscription canceled. You'll have access until the end of your billing period.
            </span>
          </div>
        )}

        {/* Already Pro */}
        {isPro && !stripeStatus && !cancelSuccess && (
          <div style={{
            padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
            background: 'rgba(124, 58, 237, 0.08)', border: '1px solid rgba(124, 58, 237, 0.2)',
            textAlign: 'center',
          }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#a78bfa' }}>
              You're on Pro
            </span>
          </div>
        )}

        <div style={styles.planSummary}>
          <div style={styles.planRow}>
            <span style={styles.planLabel}>Titler Pro</span>
            <span style={styles.planPrice}>$15/mo</span>
          </div>
          <div style={styles.planDetail}>Full AI-powered video analysis</div>
        </div>

        <ul style={styles.features}>
          {[
            'Unlimited AI-powered analysis',
            'YouTube URL transcript analysis',
            'Sentiment & tone analysis',
            'Best quotes & hook extraction',
            'SEO tag generation',
            'Analysis history & cache',
          ].map(f => (
            <li key={f} style={styles.featureItem}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="#6366f1" style={{ flexShrink: 0, marginTop: '2px' }}>
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {isPro ? (
          <>
            {user.subscriptionCurrentPeriodEnd && (
              <p style={{ margin: '0 0 16px', fontSize: '13px', color: (user.cancelAtPeriodEnd || cancelSuccess) ? '#fbbf24' : '#64748b', textAlign: 'center' }}>
                {(user.cancelAtPeriodEnd || cancelSuccess)
                  ? `Pro access ends: ${new Date(user.subscriptionCurrentPeriodEnd).toLocaleDateString()}`
                  : `Renews: ${new Date(user.subscriptionCurrentPeriodEnd).toLocaleDateString()}`}
              </p>
            )}
            {showCancelConfirm ? (
              <div style={{
                padding: '16px', borderRadius: '10px', marginBottom: '16px',
                background: 'rgba(248, 113, 113, 0.06)', border: '1px solid rgba(248, 113, 113, 0.2)',
              }}>
                <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#e2e8f0' }}>
                  Are you sure? You'll keep Pro access until the end of your billing period.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                      background: 'rgba(248, 113, 113, 0.15)', color: '#f87171',
                      fontSize: '13px', fontWeight: '600', cursor: cancelLoading ? 'wait' : 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {cancelLoading ? 'Canceling...' : 'Yes, cancel'}
                  </button>
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
                      color: '#94a3b8', fontSize: '13px', fontWeight: '600',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Keep Pro
                  </button>
                </div>
              </div>
            ) : !user.cancelAtPeriodEnd && !cancelSuccess ? (
              <button
                onClick={() => setShowCancelConfirm(true)}
                style={{
                  width: '100%', padding: '12px 20px', borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
                  color: '#94a3b8', fontSize: '14px', fontWeight: '500',
                  cursor: 'pointer', fontFamily: 'inherit',
                  marginBottom: '16px',
                }}
              >
                Cancel Subscription
              </button>
            ) : null}
          </>
        ) : (
          <button
            onClick={handleUpgrade}
            disabled={upgradeLoading}
            style={{
              ...styles.submitBtn,
              ...(upgradeLoading ? { opacity: 0.7, cursor: 'wait' } : {}),
            }}
          >
            {upgradeLoading ? 'Redirecting to Stripe...' : 'Upgrade to Pro'}
          </button>
        )}

        <p style={styles.secure}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="#64748b" style={{ flexShrink: 0 }}>
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
          </svg>
          Secure payment powered by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '80px 20px 60px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    width: '100%', maxWidth: '440px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '16px', padding: '32px 28px',
  },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'none', border: 'none', color: '#94a3b8',
    fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
    padding: 0, marginBottom: '20px',
  },
  heading: {
    margin: '0 0 20px', fontSize: '24px', fontWeight: '700',
    color: '#e2e8f0', textAlign: 'center',
  },
  planSummary: {
    padding: '16px', borderRadius: '10px',
    background: 'rgba(124,58,237,0.08)',
    border: '1px solid rgba(124,58,237,0.2)',
    marginBottom: '20px',
  },
  planRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  planLabel: {
    fontSize: '16px', fontWeight: '700', color: '#c4b5fd',
  },
  planPrice: {
    fontSize: '16px', fontWeight: '700', color: '#e2e8f0',
  },
  planDetail: {
    fontSize: '13px', color: '#94a3b8', marginTop: '4px',
  },
  features: {
    listStyle: 'none', padding: 0, margin: '0 0 24px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  featureItem: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    fontSize: '14px', color: '#cbd5e1', lineHeight: '1.4',
  },
  errorBanner: {
    padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
    background: 'rgba(248, 113, 113, 0.08)', border: '1px solid rgba(248, 113, 113, 0.2)',
    color: '#f87171', fontSize: '13px', textAlign: 'center',
  },
  submitBtn: {
    width: '100%', padding: '12px 20px', borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'opacity 0.15s',
    marginBottom: '16px',
  },
  secure: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px', margin: 0, fontSize: '12px', color: '#64748b',
  },
};

export default PaymentPage;
